import "./styles/psytest.scss";
import { useEffect, useMemo, useRef, useState } from "react";
import { QUESTIONS } from "./data/questions";
import { SCORING_MATRIX } from "./data/scoringMatrix";
import { computeFit, computeScores, pickTopCareer, computeMaxPossibleForCareer } from "./utils/scoring";

import StartPage from "./components/StartPage";
import QuestionCard from "./components/QuestionCard";
import ProgressBar from "./components/ProgressBar";
import ResultPage from "./components/ResultPage";

export default function QuizPage() {
  const total = QUESTIONS.length;

  const [stage, setStage] = useState("start");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});

  const scores = useMemo(() => computeScores(answers, SCORING_MATRIX), [answers]);
  const topCareer = useMemo(() => pickTopCareer(scores), [scores]);
  const topScore = scores[topCareer] || 0;

  const maxPossible = useMemo(
    () => computeMaxPossibleForCareer(SCORING_MATRIX, topCareer),
    [topCareer]
  );

  const STAGE_STEP = { start: 1, quiz: 2, result: 3 };
  const step = STAGE_STEP[stage] || 1;

  const fit = useMemo(() => computeFit(topScore, maxPossible), [topScore, maxPossible]);

  const result = useMemo(
    () => ({
      careerKey: topCareer,
      score: topScore,
      percentage: fit.percentage,
      range: fit.range,
      fitLabel: fit.fitLabel,
      maxPossible,
    }),
    [topCareer, topScore, fit, maxPossible]
  );

  // 中文結果名稱（給 GA 看比較直覺）
  const careerNameMap = {
    audit: "審計",
    tax: "稅務",
    fin_reporting: "企業會計",
    fpa: "FP&A",
    consulting: "顧問",
    global: "跨國發展",
    forensic: "鑑識會計",
  };

  // 避免 result event 重複送出
  const hasTrackedResultRef = useRef(false);

  // 切題/切頁自動捲到最上面
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [currentIndex, stage]);

  // 進入結果頁時送出結果事件
  useEffect(() => {
    if (stage !== "result") return;
    if (!result?.careerKey) return;
    if (hasTrackedResultRef.current) return;

    if (window.gtag) {
      window.gtag("event", "quiz_result", {
        event_category: "psytest",
        career_key: result.careerKey,
        career_name: careerNameMap[result.careerKey] || result.careerKey,
        score: result.score,
        percentage: result.percentage,
      });
    }

    hasTrackedResultRef.current = true;
  }, [stage, result]);

  function startQuiz() {
    if (window.gtag) {
      window.gtag("event", "start_quiz", {
        event_category: "psytest",
      });
    }

    hasTrackedResultRef.current = false;
    setStage("quiz");
    setCurrentIndex(0);
    setAnswers({});
  }

  function restart() {
    hasTrackedResultRef.current = false;
    setStage("start");
    setCurrentIndex(0);
    setAnswers({});
  }

  function selectAnswer(qid, optKey) {
    setAnswers((prev) => ({ ...prev, [qid]: optKey }));
  }

  function next() {
    if (currentIndex >= total - 1) {
      if (window.gtag) {
        window.gtag("event", "finish_quiz", {
          event_category: "psytest",
        });
      }

      setStage("result");
      return;
    }

    setCurrentIndex((i) => i + 1);
  }

  function prev() {
    setCurrentIndex((i) => Math.max(0, i - 1));
  }

  const currentQ = QUESTIONS[currentIndex];
  const selected = answers[currentQ?.id];

  return (
    <section className="multi_step_form quiz-page">
      <form id="msform" onSubmit={(e) => e.preventDefault()}>
        <ul id="progressbar">
          <li className={step >= 1 ? "active" : ""}>開始</li>
          <li className={step >= 2 ? "active" : ""}>測驗</li>
          <li className={step >= 3 ? "active" : ""}>結果</li>
        </ul>

        {stage === "start" && <StartPage onStart={startQuiz} />}

        {stage === "quiz" && (
          <>
            <ProgressBar currentIndex={currentIndex} total={total} />
            <QuestionCard
              question={currentQ}
              selected={selected}
              onSelect={selectAnswer}
              onPrev={prev}
              onNext={next}
              isFirst={currentIndex === 0}
              isLast={currentIndex === total - 1}
            />
          </>
        )}

        {stage === "result" && <ResultPage result={result} onRestart={restart} />}
      </form>
    </section>
  );
}